import type { Capabilities, MeResponse, Membership, WorkspaceRef } from "@ondo/shared";

import { About } from "../about/About.tsx";
import { Activity } from "../activity/Activity.tsx";
import { Notifications } from "../notifications/Notifications.tsx";
import { NotificationPreferences } from "../notificationPreferences/NotificationPreferences.tsx";
import { Profile } from "../profile/Profile.tsx";
import { PortalShell } from "../shell/PortalShell.tsx";
import { Verifications } from "../verifications/Verifications.tsx";
import { useAccount } from "../lib/account.ts";
import { ClaimStudioBanner } from "../workspace/ClaimStudioBanner.tsx";
import type { PortalView } from "../shell/shell.model.ts";
import { VERIFIER_PORTAL } from "./verifier.constants.ts";
import type { VerifierDestination } from "./verifier.constants.ts";

/**
 * The verification workspace.
 *
 * EVERY SCREEN HERE BELONGS TO ANOTHER PORTAL, and none of them is wrapped or
 * narrowed on the way in. That is the whole argument for this file being twenty
 * lines of `switch` rather than a feature folder: what an invited party may see
 * is decided by `ondo.asked_provenance_entry_ids()`, which hands them one entry
 * and stops. A verifier-shaped copy of the inbox would be a SECOND opinion about
 * that, written in TypeScript, in a place no policy can correct.
 *
 * So `Verifications` renders exactly as it does for a collector, and shows less
 * because the caller's own token reaches less. `AgentWorkspace` makes the same
 * argument in the same words, and this portal is the strongest case for it: the
 * account reading this screen has no other relation to ONDO at all.
 *
 * No `record` prop, unlike the other three portals. There is no destination here
 * that takes a fourth URL segment — see `records` in the spec — so a work id in
 * the path is a URL that does not exist rather than one this component ignores.
 */
export function VerifierWorkspace({
  destination,
  workspace,
  principal,
  capabilities,
  membership,
  canSwitch,
  onSignOut,
}: {
  destination: VerifierDestination;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  /** Null on every verifier workspace today: the capacity is only ever personal. */
  membership: Membership | null;
  canSwitch: boolean;
  onSignOut: () => void;
}) {
  return (
    <PortalShell
      spec={VERIFIER_PORTAL}
      destination={destination}
      workspace={workspace}
      principal={principal}
      capabilities={capabilities}
      membership={membership}
      canSwitch={canSwitch}
      onSignOut={onSignOut}
    >
      {(view) => (
        <Destination
          destination={destination}
          workspace={workspace}
          principal={principal}
          view={view}
          onSignOut={onSignOut}
        />
      )}
    </PortalShell>
  );
}

/** The one place a switch over the verifier's destinations lives. */
function Destination({
  destination,
  workspace,
  principal,
  view,
  onSignOut,
}: {
  destination: VerifierDestination;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  view: PortalView;
  onSignOut: () => void;
}) {
  // Read here rather than threaded through props, the way `Verifications`
  // itself reads the account: a claim answers with the whole profile, and
  // `replaceMe` is the one door through which that reaches every screen.
  const { replaceMe } = useAccount();

  switch (destination) {
    // The reader's OWN principal, always. `inboxPrincipalFor` exists to resolve
    // a firm on an organisation's workspace, and there is no organisation that
    // opens onto this portal — so calling it here would be asking a question
    // whose answer is fixed.
    case "verifications":
      // OND-8 decision 3, built as OND-7: the first screen after sign-in tells
      // the person a request about THEIR OWN WORK is waiting, by offering the
      // dormant studio a collector named at their address. Hidden for every
      // verifier who is not that person; the inbox renders beneath either way.
      return (
        <>
          <ClaimStudioBanner replaceMe={replaceMe} />
          <Verifications principalOndoId={principal.ondoId} />
        </>
      );

    case "notifications":
      return <Notifications workspace={workspace} />;

    // Whose inbox this configures is the same question the inbox itself asks,
    // answered the same way. `canWrite` is true on a personal workspace because
    // `atLeast`'s null branch is — a person always administers themselves.
    case "notification-preferences":
      return <NotificationPreferences canWrite />;

    case "about":
      return <About />;

    case "activity":
      // NO `administers`, UNLIKE THE OTHER FOUR FIRM PORTALS, and the prop
      // declaration above says why: "Null on every verifier workspace today:
      // the capacity is only ever personal." There is no firm whose trail this
      // reader could administer, so the default — "you have opened nothing" —
      // is the only true empty sentence available. `Destination` is not even
      // handed `membership`, which is the shape of that fact.
      return <Activity workBase={view.hrefs.workBase} viewerOndoId={principal.ondoId} />;

    case "profile":
      return <Profile workspace={workspace} onSignOut={onSignOut} />;
  }
}
